import React, { useMemo, useState } from 'react';
import useSeo from '../hooks/useSeo';
import { sendSlackNotification } from '../utils/slackNotifications';
import '../styles/pages/OtherProducts.css';

/* ============================================================
   Artwork
   ============================================================ */

/* Hero backdrop: dual live products - People (tracking + attendance)
   on the left, Leads (enrichment + Note Studio) on the right. */
const HERO_FONT = 'Inter, system-ui, sans-serif';
const HERO_FONT_TITLE = 'Montserrat, Inter, system-ui, sans-serif';

function HeroCard({ x, y, w, h, className = '', children }) {
  return (
    <g className={`op-node ${className}`} transform={`translate(${x} ${y})`} filter="url(#opCardShadow)">
      <rect width={w} height={h} rx="14" fill="#1b2a38" stroke="rgba(255,255,255,0.16)" />
      <rect x="0.5" y="0.5" width={w - 1} height={h - 1} rx="13.5" stroke="rgba(255,255,255,0.05)" />
      {children}
    </g>
  );
}

function HeroPill({ x, y, w, label, tone = 'blue' }) {
  const palette =
    tone === 'green'
      ? { fill: 'rgba(52,179,127,0.16)', stroke: 'rgba(52,179,127,0.4)', text: '#6fd3a6' }
      : tone === 'warm'
        ? { fill: 'rgba(232,165,75,0.16)', stroke: 'rgba(232,165,75,0.4)', text: '#f0c27a' }
        : { fill: 'rgba(107,168,212,0.14)', stroke: 'rgba(107,168,212,0.35)', text: '#9ecbff' };
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={w} height="14" rx="7" fill={palette.fill} stroke={palette.stroke} />
      <text
        x={w / 2}
        y="10"
        textAnchor="middle"
        fontFamily={HERO_FONT}
        fontSize="7.2"
        fontWeight="700"
        fill={palette.text}
      >
        {label}
      </text>
    </g>
  );
}

function HeroOps() {
  const mapX = 28;
  const mapY = 148;
  const mapW = 250;
  const mapH = 180;

  return (
    <svg className="op-hero-art" viewBox="0 0 600 470" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="opGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#6ba8d4" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#6ba8d4" stopOpacity="0" />
        </radialGradient>
        <pattern id="opGrid" width="22" height="22" patternUnits="userSpaceOnUse">
          <path d="M22 0H0V22" stroke="rgba(255,255,255,0.06)" />
        </pattern>
        <clipPath id="opMapClip">
          <rect x={mapX} y={mapY} width={mapW} height={mapH} rx="16" />
        </clipPath>
        <clipPath id="opAvatarClip">
          <circle cx="34" cy="66" r="15" />
        </clipPath>
        <filter id="opCardShadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="12" stdDeviation="12" floodColor="#000" floodOpacity="0.45" />
        </filter>
        <path id="opRoute" d={`M${mapX + 28} ${mapY + 150}C${mapX + 70} ${mapY + 130} ${mapX + 90} ${mapY + 90} ${mapX + 120} ${mapY + 78}S${mapX + 170} ${mapY + 70} ${mapX + 198} ${mapY + 40}`} />
      </defs>

      <circle cx="300" cy="230" r="210" fill="url(#opGlow)" />

      {/* ================= PEOPLE · Attendance ================= */}
      <HeroCard x={28} y={36} w={250} h={96} className="op-node--1">
        <text x="14" y="22" fontFamily={HERO_FONT_TITLE} fontSize="10" fontWeight="700" fill="#9ecbff">
          People
        </text>
        <text x="14" y="40" fontFamily={HERO_FONT_TITLE} fontSize="11" fontWeight="700" fill="#e8eef6">
          Attendance
        </text>
        <HeroPill x={176} y={12} w={60} label="Face + GPS" />

        <circle cx="34" cy="66" r="15" fill="#2a3b4d" />
        <g clipPath="url(#opAvatarClip)" fill="#9fb3c8">
          <circle cx="34" cy="61" r="5.5" />
          <path d="M20 84c2-8 7-12 14-12s12 4 14 12z" />
        </g>
        <circle cx="34" cy="66" r="19" stroke="#6ba8d4" strokeWidth="1.2" strokeDasharray="9 7" className="op-scan-ring" />

        <text x="60" y="62" fontFamily={HERO_FONT} fontSize="10" fontWeight="600" fill="#e8eef6">
          Ravi S.
        </text>
        <text x="60" y="75" fontFamily={HERO_FONT} fontSize="7.4" fill="#9fb3c8">
          08:58 · Site A · in geofence
        </text>
        <g transform="translate(168 62)">
          <rect width="52" height="14" rx="7" fill="rgba(52,179,127,0.16)" stroke="rgba(52,179,127,0.4)" />
          <path d="M8 7l2.2 2.2L14.5 5" stroke="#6fd3a6" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          <text x="31" y="10" textAnchor="middle" fontFamily={HERO_FONT} fontSize="7.2" fontWeight="700" fill="#6fd3a6">
            Verified
          </text>
        </g>
      </HeroCard>

      {/* ================= PEOPLE · Live tracking map ================= */}
      <g filter="url(#opCardShadow)">
        <rect x={mapX} y={mapY} width={mapW} height={mapH} rx="16" fill="#16232f" stroke="rgba(255,255,255,0.16)" />
      </g>
      <g clipPath="url(#opMapClip)">
        <rect x={mapX} y={mapY} width={mapW} height={mapH} fill="url(#opGrid)" />

        <g fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.07)">
          <rect x={mapX + 16} y={mapY + 20} width={44} height={32} rx="3" />
          <rect x={mapX + 76} y={mapY + 16} width={56} height={36} rx="3" />
          <rect x={mapX + 150} y={mapY + 22} width={48} height={28} rx="3" />
          <rect x={mapX + 14} y={mapY + 72} width={44} height={40} rx="3" />
          <rect x={mapX + 74} y={mapY + 74} width={60} height={36} rx="3" />
          <rect x={mapX + 152} y={mapY + 72} width={52} height={40} rx="3" />
          <rect x={mapX + 18} y={mapY + 128} width={70} height={36} rx="3" />
          <rect x={mapX + 106} y={mapY + 126} width={50} height={38} rx="3" />
          <rect x={mapX + 170} y={mapY + 124} width={46} height={40} rx="3" />
        </g>

        <g stroke="rgba(255,255,255,0.1)" strokeWidth="7">
          <path d={`M${mapX} ${mapY + 60}H${mapX + mapW}M${mapX} ${mapY + 118}H${mapX + mapW}M${mapX + 70} ${mapY}V${mapY + mapH}M${mapX + 148} ${mapY}V${mapY + mapH}`} />
        </g>
        <g stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="5 7">
          <path d={`M${mapX} ${mapY + 60}H${mapX + mapW}M${mapX} ${mapY + 118}H${mapX + mapW}M${mapX + 70} ${mapY}V${mapY + mapH}M${mapX + 148} ${mapY}V${mapY + mapH}`} />
        </g>

        <circle
          cx={mapX + 198}
          cy={mapY + 40}
          r={22}
          fill="rgba(107,168,212,0.08)"
          stroke="#6ba8d4"
          strokeWidth="1.2"
          strokeDasharray="4 4"
          opacity="0.85"
        />
        <g transform={`translate(${mapX + 198} ${mapY + 40})`}>
          <path d="M0 -10c-5 0-8 3.5-8 8 0 6 8 13 8 13s8-7 8-13c0-4.5-3-8-8-8z" fill="#6ba8d4" />
          <circle cy="-2.5" r="2.6" fill="#16232f" />
        </g>

        <use href="#opRoute" stroke="#6ba8d4" strokeWidth="2" strokeDasharray="5 6" strokeLinecap="round" opacity="0.85" />

        <g transform={`translate(${mapX + 120} ${mapY + 78})`}>
          <circle r="10" fill="rgba(62,124,177,0.22)" />
          <rect x="-7" y="-3.5" width="14" height="7" rx="1.5" fill="#3e7cb1" />
          <path d="M-4 -3.5l1.5-2.5h5L4 -3.5" fill="#3e7cb1" />
          <circle cx="-4" cy="4" r="1.6" fill="#15202b" />
          <circle cx="4" cy="4" r="1.6" fill="#15202b" />
        </g>

        <g transform={`translate(${mapX + 170} ${mapY + 70})`}>
          <circle r="9" fill="rgba(52,179,127,0.22)" />
          <circle r="6" fill="#34b37f" />
          <circle cy="-1.6" r="1.8" fill="#0f2a1e" />
          <path d="M-3.2 4c.5-2.4 1.8-3.6 3.2-3.6s2.7 1.2 3.2 3.6" fill="#0f2a1e" />
        </g>

        <g>
          <circle r="8" fill="rgba(158,203,255,0.25)" className="op-live-ring" />
          <rect x="-4" y="-6.5" width="8" height="13" rx="2" fill="#ffffff" stroke="#3e7cb1" strokeWidth="1.1" />
          <rect x="-2.4" y="-4" width="4.8" height="6.5" rx="0.8" fill="#d6e8f5" />
          <animateMotion dur="9s" repeatCount="indefinite" rotate="0">
            <mpath href="#opRoute" />
          </animateMotion>
        </g>
      </g>

      <g transform={`translate(${mapX + 12} ${mapY + 12})`}>
        <rect width="96" height="18" rx="9" fill="rgba(21,32,43,0.92)" stroke="rgba(255,255,255,0.14)" />
        <circle cx="11" cy="9" r="2.8" fill="#34b37f" />
        <circle cx="11" cy="9" r="2.8" stroke="#34b37f" strokeWidth="1.1" className="op-live-ping" />
        <text x="20" y="12.5" fontFamily={HERO_FONT} fontSize="8" fontWeight="600" fill="#e8eef6">
          Live tracking
        </text>
      </g>

      {/* ================= PEOPLE · Jobs chip ================= */}
      <HeroCard x={28} y={348} w={250} h={86} className="op-node--3">
        <text x="14" y="22" fontFamily={HERO_FONT_TITLE} fontSize="10.5" fontWeight="700" fill="#e8eef6">
          Jobs with proof
        </text>
        <HeroPill x={196} y={10} w={40} label="Done" tone="green" />

        <rect x="14" y="34" width="52" height="38" rx="8" fill="#22344a" stroke="rgba(255,255,255,0.1)" />
        <circle cx="54" cy="44" r="3.5" fill="#6ba8d4" />
        <path d="M18 66l12-14 8 8 6-6 14 12z" fill="#2f4a66" />
        <text x="78" y="48" fontFamily={HERO_FONT} fontSize="9.5" fontWeight="600" fill="#e8eef6">
          AC service
        </text>
        <text x="78" y="62" fontFamily={HERO_FONT} fontSize="7.4" fill="#9fb3c8">
          Photo · place · time · Panaji
        </text>
      </HeroCard>

      {/* ================= LEADS · Prospect enrichment ================= */}
      <HeroCard x={310} y={36} w={262} h={154} className="op-node--2">
        <text x="14" y="22" fontFamily={HERO_FONT_TITLE} fontSize="10" fontWeight="700" fill="#9ecbff">
          Leads
        </text>
        <text x="14" y="40" fontFamily={HERO_FONT_TITLE} fontSize="11" fontWeight="700" fill="#e8eef6">
          Prospect research
        </text>
        <HeroPill x={198} y={12} w={50} label="Enriched" />

        <g transform="translate(14 52)">
          <rect width="234" height="48" rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" />
          <circle cx="24" cy="24" r="14" fill="#3e7cb1" />
          <text
            x="24"
            y="24"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={HERO_FONT}
            fontSize="9"
            fontWeight="700"
            fill="#ffffff"
          >
            AK
          </text>
          <text x="48" y="18" fontFamily={HERO_FONT} fontSize="10" fontWeight="600" fill="#e8eef6">
            Asha Kapoor · Opsly
          </text>
          <text x="48" y="32" fontFamily={HERO_FONT} fontSize="7.4" fill="#9fb3c8">
            Series A · logistics SaaS · Mumbai
          </text>
          <HeroPill x={186} y={16} w={32} label="Hot" tone="green" />
        </g>

        <g transform="translate(14 112)">
          <rect width="72" height="22" rx="11" fill="rgba(107,168,212,0.12)" stroke="rgba(107,168,212,0.3)" />
          <text
            x="36"
            y="11"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={HERO_FONT}
            fontSize="7"
            fontWeight="700"
            fill="#9ecbff"
          >
            ICP fit 92%
          </text>
          <rect x="80" y="0" width="70" height="22" rx="11" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" />
          <text
            x="115"
            y="11"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={HERO_FONT}
            fontSize="7"
            fontWeight="600"
            fill="#c9d6e3"
          >
            LinkedIn
          </text>
          <rect x="158" y="0" width="76" height="22" rx="11" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" />
          <text
            x="196"
            y="11"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={HERO_FONT}
            fontSize="7"
            fontWeight="600"
            fill="#c9d6e3"
          >
            Website crawl
          </text>
        </g>
      </HeroCard>

      {/* ================= LEADS · Note Studio ================= */}
      <HeroCard x={310} y={208} w={262} h={226} className="op-node--1">
        <text x="14" y="24" fontFamily={HERO_FONT_TITLE} fontSize="11" fontWeight="700" fill="#e8eef6">
          Note Studio
        </text>
        <HeroPill x={188} y={12} w={60} label="Live notes" tone="green" />

        <g transform="translate(14 38)">
          <rect width="234" height="36" rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" />
          <circle className="op-pulse" cx="18" cy="18" r="5" fill="#ef4444" />
          <circle cx="18" cy="18" r="5" fill="#ef4444" opacity="0.35" className="op-live-ping" />
          <text x="32" y="15" fontFamily={HERO_FONT} fontSize="9" fontWeight="700" fill="#e8eef6">
            Recording
          </text>
          <text x="32" y="28" fontFamily={HERO_FONT} fontSize="7.2" fill="#9fb3c8">
            Speakerphone · 04:12 · Asha Kapoor
          </text>
        </g>

        <g transform="translate(14 86)">
          <text x="0" y="0" fontFamily={HERO_FONT} fontSize="7.2" fontWeight="600" fill="#9fb3c8">
            Transcript
          </text>
          <rect x="0" y="8" width="234" height="28" rx="8" fill="rgba(107,168,212,0.1)" stroke="rgba(107,168,212,0.25)" />
          <text x="10" y="20" fontFamily={HERO_FONT} fontSize="7.2" fontWeight="700" fill="#9ecbff">
            Asha
          </text>
          <text x="10" y="30" fontFamily={HERO_FONT} fontSize="7" fill="#c9d6e3">
            We can pilot next month if you send mid-market pricing.
          </text>
          <rect x="0" y="42" width="234" height="28" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" />
          <text x="10" y="54" fontFamily={HERO_FONT} fontSize="7.2" fontWeight="700" fill="#e8eef6">
            You
          </text>
          <text x="10" y="64" fontFamily={HERO_FONT} fontSize="7" fill="#9fb3c8">
            Got it - one-pager and three kickoff slots coming.
          </text>
        </g>

        <g transform="translate(14 168)">
          <text x="0" y="0" fontFamily={HERO_FONT} fontSize="7.2" fontWeight="600" fill="#9fb3c8">
            Auto-extracted
          </text>
          <rect x="0" y="8" width="74" height="28" rx="8" fill="rgba(52,179,127,0.14)" stroke="rgba(52,179,127,0.35)" />
          <text x="10" y="20" fontFamily={HERO_FONT} fontSize="6.6" fontWeight="700" fill="#6fd3a6">
            Decision
          </text>
          <text x="10" y="30" fontFamily={HERO_FONT} fontSize="6.4" fill="#c9d6e3">
            Pilot next mo
          </text>
          <rect x="80" y="8" width="74" height="28" rx="8" fill="rgba(107,168,212,0.12)" stroke="rgba(107,168,212,0.3)" />
          <text x="90" y="20" fontFamily={HERO_FONT} fontSize="6.6" fontWeight="700" fill="#9ecbff">
            Action
          </text>
          <text x="90" y="30" fontFamily={HERO_FONT} fontSize="6.4" fill="#c9d6e3">
            Send pricing
          </text>
          <rect x="160" y="8" width="74" height="28" rx="8" fill="rgba(232,165,75,0.12)" stroke="rgba(232,165,75,0.3)" />
          <text x="170" y="20" fontFamily={HERO_FONT} fontSize="6.6" fontWeight="700" fill="#f0c27a">
            Next
          </text>
          <text x="170" y="30" fontFamily={HERO_FONT} fontSize="6.4" fill="#c9d6e3">
            Tuesday · Rohan
          </text>
        </g>
      </HeroCard>
    </svg>
  );
}

/* People: light-theme field board - tracking map with attendance,
   photo-proof job, wages, tasks and bookings around it. */
const ILLUS_FONT = 'Inter, system-ui, sans-serif';
const ILLUS_FONT_TITLE = 'Montserrat, Inter, system-ui, sans-serif';

function IllusCard({ x, y, w, h, className = '', children }) {
  return (
    <g className={`op-float ${className}`} transform={`translate(${x} ${y})`} filter="url(#opPplShadow)">
      <rect width={w} height={h} rx="10" fill="#ffffff" stroke="#d6e8f5" />
      {children}
    </g>
  );
}

function PeopleArt() {
  return (
    <svg className="op-illus" viewBox="0 0 320 260" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="opPplWash" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f0f7fc" />
          <stop offset="100%" stopColor="#dde9f4" />
        </linearGradient>
        <pattern id="opPplGrid" width="18" height="18" patternUnits="userSpaceOnUse">
          <path d="M18 0H0V18" stroke="#cbdceb" strokeWidth="0.8" />
        </pattern>
        <clipPath id="opPplMapClip">
          <rect x="92" y="62" width="150" height="128" rx="12" />
        </clipPath>
        <filter id="opPplShadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#1c2430" floodOpacity="0.12" />
        </filter>
        <path id="opPplRoute" d="M104 178C124 160 130 132 150 124S186 122 200 100S222 84 228 78" />
      </defs>

      <rect x="18" y="18" width="284" height="224" rx="22" fill="url(#opPplWash)" />

      {/* ---- map ---- */}
      <g filter="url(#opPplShadow)">
        <rect x="92" y="62" width="150" height="128" rx="12" fill="#eef5fa" stroke="#c9dbea" />
      </g>
      <g clipPath="url(#opPplMapClip)">
        <rect x="92" y="62" width="150" height="128" fill="url(#opPplGrid)" opacity="0.7" />
        <g fill="#dbe7f1" stroke="#c9d9e6">
          <rect x="104" y="74" width="30" height="22" rx="3" />
          <rect x="150" y="72" width="38" height="26" rx="3" />
          <rect x="200" y="76" width="30" height="20" rx="3" />
          <rect x="102" y="112" width="30" height="30" rx="3" />
          <rect x="148" y="114" width="42" height="26" rx="3" />
          <rect x="204" y="112" width="30" height="30" rx="3" />
          <rect x="104" y="156" width="44" height="26" rx="3" />
          <rect x="164" y="156" width="30" height="26" rx="3" />
          <rect x="208" y="156" width="28" height="26" rx="3" />
        </g>
        <g stroke="#ffffff" strokeWidth="6">
          <path d="M92 104H242M92 148H242M140 62V190M196 62V190" />
        </g>
        <g stroke="#c9d9e6" strokeWidth="0.9" strokeDasharray="4 5">
          <path d="M92 104H242M92 148H242M140 62V190M196 62V190" />
        </g>

        {/* geofence + site pin */}
        <circle cx="228" cy="78" r="16" fill="rgba(62,124,177,0.1)" stroke="#3e7cb1" strokeWidth="1" strokeDasharray="3 3" />
        <g transform="translate(228 78)">
          <path d="M0 -8c-4 0-7 3-7 7 0 5 7 11 7 11s7-6 7-11c0-4-3-7-7-7z" fill="#3e7cb1" />
          <circle cy="-1.5" r="2" fill="#ffffff" />
        </g>

        {/* route */}
        <use href="#opPplRoute" stroke="#3e7cb1" strokeWidth="1.6" strokeDasharray="4 5" strokeLinecap="round" opacity="0.8" />

        {/* vehicle */}
        <g transform="translate(150 124)">
          <circle r="9" fill="rgba(62,124,177,0.16)" />
          <rect x="-7" y="-3" width="14" height="6" rx="1.5" fill="#3e7cb1" />
          <path d="M-4 -3l1.5-2.5h5L4 -3" fill="#3e7cb1" />
          <circle cx="-4" cy="3.5" r="1.6" fill="#15202b" />
          <circle cx="4" cy="3.5" r="1.6" fill="#15202b" />
        </g>

        {/* person */}
        <g transform="translate(200 100)">
          <circle r="8" fill="rgba(52,179,127,0.18)" />
          <circle r="5" fill="#34b37f" />
          <circle cy="-1.5" r="1.6" fill="#0f2a1e" />
          <path d="M-3 3.5c.5-2 1.6-3 3-3s2.5 1 3 3" fill="#0f2a1e" />
        </g>

        {/* phone on the move */}
        <g>
          <circle r="7" fill="rgba(62,124,177,0.2)" className="op-live-ring" />
          <rect x="-3.5" y="-6" width="7" height="12" rx="1.8" fill="#ffffff" stroke="#3e7cb1" strokeWidth="1" />
          <animateMotion dur="8s" repeatCount="indefinite" rotate="0">
            <mpath href="#opPplRoute" />
          </animateMotion>
        </g>
      </g>

      {/* map label */}
      <g transform="translate(136 92)">
        <rect width="60" height="14" rx="7" fill="#ffffff" stroke="#d6e8f5" />
        <circle cx="8" cy="7" r="2.4" fill="#34b37f" />
        <circle cx="8" cy="7" r="2.4" stroke="#34b37f" strokeWidth="1" className="op-live-ping" />
        <text x="15" y="9.6" fontFamily={ILLUS_FONT} fontSize="6.2" fontWeight="600" fill="#1c2430">
          Live tracking
        </text>
      </g>

      {/* ---- attendance ---- */}
      <IllusCard x={30} y={34} w={104} h={54} className="op-float--a">
        <text x="9" y="14" fontFamily={ILLUS_FONT_TITLE} fontSize="7.2" fontWeight="700" fill="#1c2430">
          Attendance
        </text>
        <circle cx="19" cy="34" r="9" fill="#d6e8f5" />
        <circle cx="19" cy="31" r="3.4" fill="#5b86ad" />
        <path d="M11.5 43c1-4.5 3.8-6.8 7.5-6.8s6.5 2.3 7.5 6.8z" fill="#5b86ad" />
        <circle cx="19" cy="34" r="11.5" stroke="#3e7cb1" strokeWidth="0.9" strokeDasharray="5 4" className="op-scan-ring" />
        <text x="35" y="32" fontFamily={ILLUS_FONT} fontSize="6.6" fontWeight="600" fill="#1c2430">
          Ravi S.
        </text>
        <text x="35" y="40" fontFamily={ILLUS_FONT} fontSize="5.4" fill="#6b7684">
          08:58 · Site A
        </text>
        <rect x="35" y="43" width="30" height="8" rx="4" fill="#ddf3e8" />
        <text x="50" y="49" textAnchor="middle" fontFamily={ILLUS_FONT} fontSize="4.8" fontWeight="700" fill="#1f7a52">
          VERIFIED
        </text>
      </IllusCard>

      {/* ---- jobs with proof ---- */}
      <IllusCard x={196} y={30} w={96} h={54} className="op-float--b">
        <text x="9" y="14" fontFamily={ILLUS_FONT_TITLE} fontSize="7.2" fontWeight="700" fill="#1c2430">
          Jobs with proof
        </text>
        <rect x="9" y="20" width="34" height="26" rx="4" fill="#dbe7f1" />
        <circle cx="36" cy="27" r="2.6" fill="#3e7cb1" />
        <path d="M11 44l9-11 6 6 4-4 11 9z" fill="#5b86ad" />
        <rect x="9" y="40" width="34" height="6" fill="rgba(28,36,48,0.75)" />
        <text x="26" y="44.6" textAnchor="middle" fontFamily={ILLUS_FONT} fontSize="4" fontWeight="600" fill="#ffffff">
          14:22 · Panaji
        </text>
        <text x="49" y="29" fontFamily={ILLUS_FONT} fontSize="6.2" fontWeight="600" fill="#1c2430">
          AC service
        </text>
        <rect x="49" y="34" width="26" height="8" rx="4" fill="#ddf3e8" />
        <text x="62" y="40" textAnchor="middle" fontFamily={ILLUS_FONT} fontSize="4.8" fontWeight="700" fill="#1f7a52">
          DONE
        </text>
      </IllusCard>

      {/* ---- tasks ---- */}
      <IllusCard x={26} y={166} w={90} h={62} className="op-float--b">
        <text x="9" y="14" fontFamily={ILLUS_FONT_TITLE} fontSize="7.2" fontWeight="700" fill="#1c2430">
          Tasks
        </text>
        <g fontFamily={ILLUS_FONT} fontSize="4.4" fontWeight="600" fill="#6b7684">
          <text x="9" y="24">TO DO</text>
          <text x="36" y="24">DOING</text>
          <text x="63" y="24">DONE</text>
        </g>
        <g fill="#f0f7fc" stroke="#d6e8f5">
          <rect x="9" y="28" width="22" height="8" rx="2" className="op-card op-card--1" />
          <rect x="9" y="39" width="22" height="8" rx="2" className="op-card op-card--2" />
          <rect x="9" y="50" width="22" height="8" rx="2" className="op-card op-card--3" />
          <rect x="36" y="28" width="22" height="8" rx="2" className="op-card op-card--2" />
          <rect x="36" y="39" width="22" height="8" rx="2" className="op-card op-card--1" />
        </g>
        <g fill="#ddf3e8" stroke="#b9e4cf">
          <rect x="63" y="28" width="22" height="8" rx="2" />
          <rect x="63" y="39" width="22" height="8" rx="2" />
        </g>
        <g stroke="#1f7a52" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
          <path d="M67 32l1.5 1.5 3-3" />
          <path d="M67 43l1.5 1.5 3-3" />
        </g>
      </IllusCard>

      {/* ---- wages ---- */}
      <IllusCard x={210} y={162} w={84} h={66} className="op-float--a">
        <text x="9" y="14" fontFamily={ILLUS_FONT_TITLE} fontSize="7.2" fontWeight="700" fill="#1c2430">
          Wages
        </text>
        <text x="9" y="30" fontFamily={ILLUS_FONT_TITLE} fontSize="11" fontWeight="800" fill="#1c2430" letterSpacing="-0.3">
          ₹12,400
        </text>
        <text x="9" y="38" fontFamily={ILLUS_FONT} fontSize="4.8" fill="#6b7684">
          6 days · advance -₹2,000
        </text>
        <g>
          <rect className="op-vbar" x="9" y="48" width="7" height="12" rx="2" fill="#3e7cb1" />
          <rect className="op-vbar" x="20" y="45" width="7" height="15" rx="2" fill="#3e7cb1" />
          <rect className="op-vbar" x="31" y="47" width="7" height="13" rx="2" fill="#3e7cb1" />
          <rect x="42" y="57" width="7" height="3" rx="1.5" fill="#e2e8f0" />
          <rect className="op-vbar" x="53" y="44" width="7" height="16" rx="2" fill="#3e7cb1" />
          <rect className="op-vbar" x="64" y="46" width="7" height="14" rx="2" fill="#3e7cb1" />
        </g>
      </IllusCard>

      {/* ---- bookings ---- */}
      <IllusCard x={118} y={196} w={110} h={40} className="op-float--b">
        <text x="8" y="12" fontFamily={ILLUS_FONT_TITLE} fontSize="6.6" fontWeight="700" fill="#1c2430">
          Bookings
        </text>
        <rect x="8" y="17" width="30" height="17" rx="4" fill="#ddf3e8" stroke="#b9e4cf" />
        <text x="12" y="24.5" fontFamily={ILLUS_FONT} fontSize="4.4" fontWeight="700" fill="#1f7a52">
          9:00-10:30
        </text>
        <text x="12" y="30.5" fontFamily={ILLUS_FONT} fontSize="4" fill="#4a5568">
          Team A
        </text>
        <rect x="41" y="17" width="30" height="17" rx="4" fill="#f0f7fc" stroke="#3e7cb1" strokeDasharray="2.5 2" />
        <text x="45" y="24.5" fontFamily={ILLUS_FONT} fontSize="4.4" fontWeight="700" fill="#2a5a82">
          11:00 open
        </text>
        <text x="45" y="30.5" fontFamily={ILLUS_FONT} fontSize="4" fill="#4a5568">
          Tap to book
        </text>
        <rect x="74" y="17" width="28" height="17" rx="4" fill="#f7f9fb" stroke="#e2e8f0" />
        <text x="78" y="24.5" fontFamily={ILLUS_FONT} fontSize="4.4" fontWeight="700" fill="#1c2430">
          2:00-4:00
        </text>
        <text x="78" y="30.5" fontFamily={ILLUS_FONT} fontSize="4" fill="#4a5568">
          Vikram
        </text>
      </IllusCard>
    </svg>
  );
}

/* Expenses: receipt being scanned into categorised bars */
function ExpensesArt() {
  return (
    <svg className="op-illus" viewBox="0 0 320 260" fill="none" aria-hidden="true">
      <rect x="18" y="18" width="284" height="224" rx="22" fill="#f7f9fb" />

      {/* receipt */}
      <g transform="translate(78 44)">
        <path
          d="M0 8a8 8 0 0 1 8-8h84a8 8 0 0 1 8 8v152l-10-7-10 7-10-7-10 7-10-7-10 7-10-7-10 7-10-7-10 7z"
          fill="#ffffff"
          stroke="#cbd5e1"
          strokeWidth="1.5"
        />
        <rect x="18" y="20" width="44" height="5" rx="2.5" fill="#1c2430" />
        <rect x="18" y="34" width="64" height="3.5" rx="1.75" fill="#e2e8f0" />
        <rect x="18" y="52" width="40" height="3.5" rx="1.75" fill="#94a3b8" />
        <rect x="68" y="52" width="14" height="3.5" rx="1.75" fill="#2a5a82" />
        <rect x="18" y="66" width="46" height="3.5" rx="1.75" fill="#94a3b8" />
        <rect x="70" y="66" width="12" height="3.5" rx="1.75" fill="#2a5a82" />
        <rect x="18" y="80" width="36" height="3.5" rx="1.75" fill="#94a3b8" />
        <rect x="66" y="80" width="16" height="3.5" rx="1.75" fill="#2a5a82" />
        <path d="M18 98h64" stroke="#e2e8f0" strokeDasharray="3 3" />
        <rect x="18" y="108" width="30" height="5" rx="2.5" fill="#1c2430" />
        <rect x="60" y="108" width="22" height="5" rx="2.5" fill="#2a5a82" />
        <rect x="26" y="126" width="48" height="16" rx="3" fill="#f0f7fc" />
        <g stroke="#2a5a82" strokeWidth="1.4">
          <path d="M30 130v8M34 130v8M38 130v8M42 130v8M48 130v8M52 130v8M58 130v8M62 130v8M68 130v8" />
        </g>
      </g>

      {/* scan line */}
      <rect className="op-scan" x="72" y="60" width="112" height="3" rx="1.5" fill="#3e7cb1" opacity="0.7" />

      {/* categorised bars */}
      <g transform="translate(208 68)">
        <rect x="0" y="0" width="70" height="120" rx="12" fill="#ffffff" stroke="#e2e8f0" />
        <rect x="12" y="14" width="30" height="4" rx="2" fill="#1c2430" />
        <rect className="op-bar op-bar--1" x="12" y="30" width="46" height="8" rx="4" fill="#3e7cb1" />
        <rect className="op-bar op-bar--2" x="12" y="46" width="28" height="8" rx="4" fill="#6ba8d4" />
        <rect className="op-bar op-bar--3" x="12" y="62" width="38" height="8" rx="4" fill="#2a5a82" />
        <rect className="op-bar op-bar--4" x="12" y="78" width="18" height="8" rx="4" fill="#d6e8f5" />
        <rect x="12" y="98" width="46" height="4" rx="2" fill="#e2e8f0" />
      </g>

      {/* arrow */}
      <path d="M186 128h16" stroke="#3e7cb1" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M198 123l5 5-5 5" stroke="#3e7cb1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />

      {/* coin */}
      <g className="op-float op-float--b" transform="translate(52 190)">
        <circle r="18" fill="#ffffff" stroke="#3e7cb1" strokeWidth="2" />
        <path d="M-4 -6h8M-4 0h8M-4 6h5" stroke="#2a5a82" strokeWidth="1.8" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/* Note Studio: personal / call / room capture → MoM, plan, summary */
function NoteStudioArt() {
  return (
    <svg className="op-illus" viewBox="0 0 320 260" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="opNotesWash" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f0f7fc" />
          <stop offset="100%" stopColor="#dde9f4" />
        </linearGradient>
        <filter id="opNotesShadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#1c2430" floodOpacity="0.12" />
        </filter>
      </defs>

      <rect x="18" y="18" width="284" height="224" rx="22" fill="url(#opNotesWash)" />

      <g filter="url(#opNotesShadow)" transform="translate(40 36)">
        <rect width="240" height="188" rx="14" fill="#ffffff" stroke="#d6e8f5" />

        {/* window chrome */}
        <circle cx="16" cy="16" r="2.4" fill="#f87171" />
        <circle cx="24" cy="16" r="2.4" fill="#fbbf24" />
        <circle cx="32" cy="16" r="2.4" fill="#34b37f" />
        <text
          x="44"
          y="16"
          dominantBaseline="central"
          fontFamily={ILLUS_FONT}
          fontSize="7"
          fontWeight="600"
          fill="#5b86ad"
        >
          Meeting room · Live
        </text>

        {/* mode chips */}
        <g transform="translate(12 30)">
          <rect width="52" height="16" rx="8" fill="#f0f7fc" stroke="#d6e8f5" />
          <text
            x="26"
            y="8"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={ILLUS_FONT}
            fontSize="5.8"
            fontWeight="600"
            fill="#2a5a82"
          >
            Personal
          </text>
          <rect x="58" y="0" width="40" height="16" rx="8" fill="rgba(62,124,177,0.12)" stroke="rgba(62,124,177,0.35)" />
          <text
            x="78"
            y="8"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={ILLUS_FONT}
            fontSize="5.8"
            fontWeight="700"
            fill="#2a5a82"
          >
            Call
          </text>
          <rect x="104" y="0" width="48" height="16" rx="8" fill="rgba(52,179,127,0.12)" stroke="rgba(52,179,127,0.35)" />
          <text
            x="128"
            y="8"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={ILLUS_FONT}
            fontSize="5.8"
            fontWeight="700"
            fill="#1f7a52"
          >
            Room
          </text>
        </g>

        {/* recording + transcript */}
        <g transform="translate(12 56)">
          <rect width="216" height="28" rx="8" fill="#f8fafc" stroke="#e2e8f0" />
          <circle className="op-pulse" cx="14" cy="14" r="4" fill="#ef4444" />
          <circle cx="14" cy="14" r="4" fill="#ef4444" opacity="0.35" className="op-live-ping" />
          <text x="26" y="11" fontFamily={ILLUS_FONT} fontSize="6.4" fontWeight="700" fill="#1c2430">
            Recording
          </text>
          <text x="26" y="21" fontFamily={ILLUS_FONT} fontSize="5.6" fill="#94a3b8">
            3 speakers · 18:42
          </text>
        </g>

        <g transform="translate(12 94)">
          <rect width="216" height="36" rx="8" fill="#f0f7fc" />
          <rect x="8" y="8" width="120" height="4" rx="2" fill="#b9d3ea" />
          <rect x="8" y="16" width="160" height="4" rx="2" fill="#d6e8f5" />
          <rect x="8" y="24" width="90" height="4" rx="2" fill="#d6e8f5" />
        </g>

        {/* outputs */}
        <g transform="translate(12 140)">
          <text x="0" y="0" fontFamily={ILLUS_FONT} fontSize="5.8" fontWeight="600" fill="#94a3b8">
            From this session
          </text>
          <rect x="0" y="8" width="68" height="28" rx="8" fill="rgba(52,179,127,0.12)" stroke="rgba(52,179,127,0.35)" />
          <text x="10" y="20" fontFamily={ILLUS_FONT} fontSize="6.2" fontWeight="700" fill="#1f7a52">
            MoM
          </text>
          <text x="10" y="29" fontFamily={ILLUS_FONT} fontSize="5.4" fill="#5b86ad">
            Decisions
          </text>
          <rect x="74" y="8" width="68" height="28" rx="8" fill="rgba(62,124,177,0.1)" stroke="rgba(62,124,177,0.35)" />
          <text x="84" y="20" fontFamily={ILLUS_FONT} fontSize="6.2" fontWeight="700" fill="#2a5a82">
            Plan
          </text>
          <text x="84" y="29" fontFamily={ILLUS_FONT} fontSize="5.4" fill="#5b86ad">
            From ideas
          </text>
          <rect x="148" y="8" width="68" height="28" rx="8" fill="#fffaf3" stroke="#f3e2c7" />
          <text x="158" y="20" fontFamily={ILLUS_FONT} fontSize="6.2" fontWeight="700" fill="#9a7a45">
            Summary
          </text>
          <text x="158" y="29" fontFamily={ILLUS_FONT} fontSize="5.4" fill="#9a7a45">
            + context
          </text>
        </g>
      </g>
    </svg>
  );
}

/* Leads: enriched prospect research + live Note Studio transcription */
function LeadsChip({ x, y, w, h, label, fill = '#ffffff', stroke = '#d6e8f5', color = '#2a5a82' }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={w} height={h} rx={h / 2} fill={fill} stroke={stroke} />
      <text
        x={w / 2}
        y={h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={ILLUS_FONT}
        fontSize="5.8"
        fontWeight="600"
        fill={color}
      >
        {label}
      </text>
    </g>
  );
}

function LeadsArt() {
  const pad = 10;
  const cardH = 196;
  const cardY = 32;
  const leftW = 152;
  const rightW = 100;
  const leftX = 30;
  const rightX = 190;
  const innerW = leftW - pad * 2; // 132

  return (
    <svg className="op-illus" viewBox="0 0 320 260" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="opLeadsWash" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f0f7fc" />
          <stop offset="100%" stopColor="#dde9f4" />
        </linearGradient>
        <filter id="opLeadsShadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#1c2430" floodOpacity="0.12" />
        </filter>
      </defs>

      <rect x="18" y="18" width="284" height="224" rx="22" fill="url(#opLeadsWash)" />

      {/* ---- enriched prospect card ---- */}
      <g filter="url(#opLeadsShadow)" transform={`translate(${leftX} ${cardY})`}>
        <rect width={leftW} height={cardH} rx="14" fill="#ffffff" stroke="#d6e8f5" />

        <text
          x={pad}
          y="18"
          fontFamily={ILLUS_FONT_TITLE}
          fontSize="7"
          fontWeight="700"
          fill="#5b86ad"
        >
          Prospect research
        </text>

        {/* primary lead */}
        <g transform={`translate(${pad} 26)`}>
          <rect width={innerW} height="52" rx="10" fill="#f0f7fc" stroke="#d6e8f5" />
          <circle cx="22" cy="26" r="13" fill="#3e7cb1" />
          <text
            x="22"
            y="26"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={ILLUS_FONT}
            fontSize="8"
            fontWeight="700"
            fill="#ffffff"
          >
            AK
          </text>
          <text x="42" y="18" fontFamily={ILLUS_FONT_TITLE} fontSize="8" fontWeight="700" fill="#1c2430">
            Asha Kapoor
          </text>
          <text x="42" y="30" fontFamily={ILLUS_FONT} fontSize="6" fill="#5b86ad">
            Opsly · Mumbai
          </text>
          <LeadsChip
            x={42}
            y={36}
            w={30}
            h={12}
            label="Hot"
            fill="rgba(52,179,127,0.16)"
            stroke="rgba(52,179,127,0.4)"
            color="#1f7a52"
          />
        </g>

        {/* enrichment chips - 2×2 grid */}
        <g transform={`translate(${pad} 88)`}>
          <text x="0" y="0" fontFamily={ILLUS_FONT} fontSize="6" fontWeight="600" fill="#94a3b8">
            Enriched context
          </text>
          {(() => {
            const gap = 6;
            const chipH = 16;
            const chipW = (innerW - gap) / 2;
            const row2 = 8 + chipH + gap;
            return (
              <>
                <LeadsChip x={0} y={8} w={chipW} h={chipH} label="Series A" />
                <LeadsChip x={chipW + gap} y={8} w={chipW} h={chipH} label="Logistics SaaS" />
                <LeadsChip x={0} y={row2} w={chipW} h={chipH} label="ICP fit 92%" />
                <LeadsChip x={chipW + gap} y={row2} w={chipW} h={chipH} label="LinkedIn" />
              </>
            );
          })()}
        </g>

        {/* secondary warm row */}
        <g transform={`translate(${pad} 156)`}>
          <rect width={innerW} height="30" rx="8" fill="#fffaf3" stroke="#f3e2c7" />
          <circle cx="16" cy="15" r="8" fill="#e8a54b" />
          <text
            x="16"
            y="15"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={ILLUS_FONT}
            fontSize="6"
            fontWeight="700"
            fill="#ffffff"
          >
            RN
          </text>
          <text x="30" y="12" fontFamily={ILLUS_FONT} fontSize="6.4" fontWeight="600" fill="#1c2430">
            Rohan · Fieldstack
          </text>
          <text x="30" y="23" fontFamily={ILLUS_FONT} fontSize="5.6" fill="#9a7a45">
            Warm · hiring SDRs
          </text>
        </g>
      </g>

      {/* ---- Note Studio panel ---- */}
      <g filter="url(#opLeadsShadow)" transform={`translate(${rightX} ${cardY})`}>
        <rect width={rightW} height={cardH} rx="14" fill="#ffffff" stroke="#d6e8f5" />

        <circle cx="14" cy="14" r="2.2" fill="#f87171" />
        <circle cx="22" cy="14" r="2.2" fill="#fbbf24" />
        <circle cx="30" cy="14" r="2.2" fill="#34b37f" />
        <text
          x="40"
          y="14"
          dominantBaseline="central"
          fontFamily={ILLUS_FONT}
          fontSize="6.2"
          fontWeight="600"
          fill="#5b86ad"
        >
          Live notes
        </text>

        {/* recording */}
        <g transform={`translate(${pad} 24)`}>
          <rect width={rightW - pad * 2} height="26" rx="8" fill="#f8fafc" stroke="#e2e8f0" />
          <circle className="op-pulse" cx="12" cy="13" r="3.5" fill="#ef4444" />
          <circle cx="12" cy="13" r="3.5" fill="#ef4444" opacity="0.35" className="op-live-ping" />
          <text x="22" y="10" fontFamily={ILLUS_FONT} fontSize="6" fontWeight="700" fill="#1c2430">
            Recording
          </text>
          <text x="22" y="20" fontFamily={ILLUS_FONT} fontSize="5.4" fill="#94a3b8">
            Speaker · 04:12
          </text>
        </g>

        {/* transcript */}
        <g transform={`translate(${pad} 58)`}>
          <text x="0" y="0" fontFamily={ILLUS_FONT} fontSize="5.6" fontWeight="600" fill="#94a3b8">
            Transcript
          </text>
          <rect x="0" y="6" width={rightW - pad * 2} height="22" rx="6" fill="#f0f7fc" />
          <text x="6" y="13" fontFamily={ILLUS_FONT} fontSize="5.2" fontWeight="700" fill="#3e7cb1">
            Asha
          </text>
          <text x="6" y="22" fontFamily={ILLUS_FONT} fontSize="5" fill="#5b86ad">
            Pilot next month…
          </text>
          <rect x="0" y="32" width={rightW - pad * 2} height="22" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
          <text x="6" y="39" fontFamily={ILLUS_FONT} fontSize="5.2" fontWeight="700" fill="#1c2430">
            You
          </text>
          <text x="6" y="48" fontFamily={ILLUS_FONT} fontSize="5" fill="#64748b">
            Send one-pager + slots
          </text>
        </g>

        {/* auto-extracted - fits inside card with bottom pad */}
        <g transform={`translate(${pad} 120)`}>
          <text x="0" y="0" fontFamily={ILLUS_FONT} fontSize="5.6" fontWeight="600" fill="#94a3b8">
            Auto-extracted
          </text>
          <LeadsChip
            x={0}
            y={6}
            w={rightW - pad * 2}
            h={16}
            label="Decision · Pilot"
            fill="rgba(52,179,127,0.12)"
            stroke="rgba(52,179,127,0.35)"
            color="#1f7a52"
          />
          <LeadsChip
            x={0}
            y={26}
            w={rightW - pad * 2}
            h={16}
            label="Action · Pricing"
            fill="rgba(62,124,177,0.1)"
            stroke="rgba(62,124,177,0.35)"
            color="#2a5a82"
          />
          <LeadsChip
            x={0}
            y={46}
            w={rightW - pad * 2}
            h={16}
            label="Next · Tuesday"
            fill="#fffaf3"
            stroke="#f3e2c7"
            color="#9a7a45"
          />
        </g>
      </g>
    </svg>
  );
}

/* ============================================================
   Small glyphs
   ============================================================ */

function IconArrow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 13 13 3M6 3h7v7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 11V7a4 4 0 0 1 8 0v4l1 1.5H3L4 11ZM6.5 13.5a1.5 1.5 0 0 0 3 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ============================================================
   Content
   ============================================================ */

const LIVE_PRODUCTS = [
  {
    id: 'people',
    name: 'BloomNav People',
    tagline: 'Who showed up. Where they went. What got done.',
    blurb:
      'Attendance with face match and geofence, live tracking of vehicles and phones, photo-proof jobs, wages, tasks, and bookings. One app for field teams, sites, and branches.',
    href: 'https://people.bloomnav.com/',
    host: 'people.bloomnav.com',
    features: [
      'Attendance with face verification + location',
      'Live tracking of vehicles, phones, and people',
      'Jobs with photo proof, stamped with place and time',
      'Wage tracking with advances and deductions',
      'Task management: list, map, kanban, calendar',
      'Bookings and a shared attendance kiosk',
    ],
    art: <PeopleArt />,
  },
  {
    id: 'leads',
    name: 'BloomNav Leads',
    tagline: 'Enrich every prospect. Transcribe every call.',
    blurb:
      'Research B2B leads with company context, then put the call on speaker - Note Studio transcribes live and pulls out decisions, action items, and follow-ups.',
    href: 'https://leads.bloomnav.com/',
    host: 'leads.bloomnav.com',
    features: [
      'Lead enrichment with context relevant to your business',
      'Scores against your ideal customer profile',
      'Live call transcription on the prospect',
      'Auto-extracted decisions, actions, and follow-ups',
      'Notes tied to each prospect',
    ],
    art: <LeadsArt />,
  },
];

const UPCOMING_PRODUCTS = [
  {
    id: 'expenses',
    name: 'BloomNav Expenses',
    tagline: 'Every receipt, captured and categorised.',
    blurb:
      'Snap a receipt, let it read itself, and reconcile against bank statements. Approvals and exports built for finance teams.',
    features: ['Receipt OCR', 'Duplicate detection', 'Statement reconciliation'],
    stage: 'In build',
    art: <ExpensesArt />,
  },
  {
    id: 'note-studio',
    name: 'BloomNav Note Studio',
    tagline: 'Conversations, turned into something you can act on.',
    blurb:
      'Transcribe personal notes, phone calls, or meeting rooms. Get MoMs, plans from ideas, clear breakdowns and summaries.',
    features: [
      'Minutes of meeting (MoM)',
      'Plans generated from spoken ideas',
      'Meeting breakdowns and summaries',
      'Context that builds',
    ],
    stage: 'Planned',
    art: <NoteStudioArt />,
  },
];

const PRINCIPLES = [
  {
    name: 'Focused, not bloated',
    detail: 'Each product does one job well. No feature sprawl, no menus you never open.',
  },
  {
    name: 'Built by the studio',
    detail: 'The same team that ships custom software for clients builds and runs these products.',
  },
  {
    name: 'Your data stays yours',
    detail: 'Exports on demand, clear deletion, and no lock-in. Leave whenever you like.',
  },
];

/* ============================================================
   Pre-register form
   ============================================================ */

function WaitlistForm({ product }) {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');

    const res = await sendSlackNotification(
      { product: product.name, email: email.trim(), companyName: companyName.trim(), source: 'bloomnav.com/products' },
      'waitlist'
    );

    if (res) {
      setStatus('done');
    } else {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="op-wait-done" role="status">
        <span className="op-wait-done-mark" aria-hidden="true">
          <IconCheck />
        </span>
        <div>
          <strong>You&rsquo;re on the list.</strong>
          <p>We&rsquo;ll email you when {product.name} opens up. No drip campaigns, just the one note.</p>
        </div>
      </div>
    );
  }

  const emailId = `op-email-${product.id}`;
  const companyId = `op-company-${product.id}`;

  return (
    <form className="op-wait" onSubmit={handleSubmit}>
      <div className="op-wait-fields">
        <div className="op-wait-field">
          <label htmlFor={emailId}>Work email</label>
          <input
            id={emailId}
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            required
          />
        </div>
        <div className="op-wait-field">
          <label htmlFor={companyId}>Company</label>
          <input
            id={companyId}
            type="text"
            name="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Optional"
            autoComplete="organization"
          />
        </div>
      </div>
      <button type="submit" className="op-btn op-btn--primary" disabled={status === 'sending'}>
        <IconBell />
        {status === 'sending' ? 'Saving…' : 'Pre-register'}
      </button>
      {status === 'error' && (
        <p className="op-wait-error" role="alert">
          Something went wrong. Try again, or email <a href="mailto:support@bloomnav.com">support@bloomnav.com</a>.
        </p>
      )}
    </form>
  );
}

/* ============================================================
   Page
   ============================================================ */

function OtherProducts() {
  const seoConfig = useMemo(() => {
    const title = 'Products | BloomNav';
    const description =
      'Products built and run by BloomNav: People for attendance and workforce, and Leads for enrichment and live call notes, are live today. Expenses and Note Studio are on the way - pre-register to hear first.';
    return {
      title,
      description,
      canonicalPath: '/products',
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav',
      jsonLd: [
        {
          id: 'schema-products-list',
          data: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Products by BloomNav',
            itemListElement: [
              ...LIVE_PRODUCTS.map((p, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'SoftwareApplication',
                  name: p.name,
                  applicationCategory: 'BusinessApplication',
                  operatingSystem: 'Web, iOS, Android',
                  url: p.href,
                  description: p.blurb,
                },
              })),
              ...UPCOMING_PRODUCTS.map((p, i) => ({
                '@type': 'ListItem',
                position: LIVE_PRODUCTS.length + i + 1,
                item: {
                  '@type': 'SoftwareApplication',
                  name: p.name,
                  applicationCategory: 'BusinessApplication',
                  description: p.blurb,
                },
              })),
            ],
          },
        },
      ],
    };
  }, []);

  useSeo(seoConfig);

  return (
    <div className="op-page">
      {/* ================= HERO ================= */}
      <section className="op-hero" data-nav-tone="dark">
        <div className="op-hero-inner">
          <div className="op-hero-copy">
            <span className="op-kicker op-kicker--light">Products by BloomNav</span>
            <h1 className="op-hero-title">
              Built in the studio.
              <br />
              <em>Ready to run.</em>
            </h1>
            <p className="op-hero-lede">
              Alongside custom builds, we ship a small family of focused products for teams that
              need attendance, expenses, and leads handled today, not in four months.
            </p>
            <div className="op-hero-actions">
              <a className="op-btn op-btn--paper" href="#live">
                See live products
                <IconArrow />
              </a>
              <a className="op-btn op-btn--ghost" href="#upcoming">
                See what&rsquo;s next
              </a>
            </div>
            <ul className="op-hero-legend" aria-label="Product status legend">
              <li>
                <span className="op-dot op-dot--live" aria-hidden="true" />
                {LIVE_PRODUCTS.length} live
              </li>
              <li>
                <span className="op-dot op-dot--soon" aria-hidden="true" />
                {UPCOMING_PRODUCTS.length} coming soon
              </li>
            </ul>
          </div>
          <div className="op-hero-visual" aria-hidden="true">
            <HeroOps />
          </div>
        </div>
      </section>

      {/* ================= LIVE ================= */}
      <section className="op-section op-live" id="live" data-nav-tone="light">
        <header className="op-section-head">
          <span className="op-kicker">Available now</span>
          <h2>
            Live <em>today.</em>
          </h2>
        </header>

        <div className="op-live-stack">
          {LIVE_PRODUCTS.map((product) => (
            <article className="op-feature" key={product.id}>
              <div className="op-feature-art">{product.art}</div>
              <div className="op-feature-copy">
                <div className="op-feature-head">
                  <span className="op-status op-status--live">
                    <span className="op-dot op-dot--live" aria-hidden="true" />
                    Live
                  </span>
                  <h3>{product.name}</h3>
                  <p className="op-feature-tagline">{product.tagline}</p>
                </div>
                <p className="op-feature-blurb">{product.blurb}</p>
                <ul className="op-feature-list">
                  {product.features.map((f) => (
                    <li key={f}>
                      <span className="op-feature-tick" aria-hidden="true">
                        <IconCheck />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="op-feature-actions">
                  <a
                    className="op-btn op-btn--primary"
                    href={product.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open {product.name}
                    <IconArrow />
                  </a>
                  <span className="op-feature-url">{product.host}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ================= UPCOMING ================= */}
      <section className="op-section op-upcoming" id="upcoming" data-nav-tone="light">
        <header className="op-section-head">
          <span className="op-kicker">On the way</span>
          <h2>
            Coming <em>soon.</em>
          </h2>
          <p>
            Two more products are in the works. Pre-register and we&rsquo;ll let you know the moment
            they open, with early access for teams that sign up first.
          </p>
        </header>

        <div className="op-upcoming-grid">
          {UPCOMING_PRODUCTS.map((p) => (
            <article className="op-card" key={p.id}>
              <div className="op-card-art">
                {p.art}
                <span className="op-status op-status--soon">
                  <span className="op-dot op-dot--soon" aria-hidden="true" />
                  {p.stage}
                </span>
              </div>
              <div className="op-card-body">
                <h3>{p.name}</h3>
                <p className="op-card-tagline">{p.tagline}</p>
                <p className="op-card-blurb">{p.blurb}</p>
                <ul className="op-chips" aria-label={`${p.name} features`}>
                  {p.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <WaitlistForm product={p} />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ================= PRINCIPLES ================= */}
      <section className="op-section op-principles" data-nav-tone="dark">
        <div className="op-principles-inner">
          <header className="op-section-head op-section-head--dark">
            <span className="op-kicker op-kicker--light">How we build products</span>
            <h2>
              Small tools, <em>well made.</em>
            </h2>
          </header>
          <ol className="op-principle-list">
            {PRINCIPLES.map((item, i) => (
              <li key={item.name}>
                <span className="op-principle-num">0{i + 1}</span>
                <h3>{item.name}</h3>
                <p>{item.detail}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}

export default OtherProducts;
